// The lines an imported Vision LINE button returns (despatch Default / Lot No,
// adj_iss Stock …), set up the way Vision's MasterStatefulEJB writes a record
// it adds — shared by the line form (ChildFormTab) and the spreadsheet's line
// grid (ChildTabularGrid), so the two cannot drift apart.
//
// Pure async helpers: each surface supplies its own transport (`exec`) and
// owns its own rows; nothing here touches React state.
import type { ColumnDef, SqlModel } from '../types'
import { normalizeItemChange } from '../types'
import { columnDbName, columnFormKey, literalDefaults } from '../utils'
import { isInvokableRule } from '../list/useItemChange'

/** How a surface runs an engine action for its object (item_change, row_add). */
export type LineExec = (action: string, payload: Record<string, unknown>) => Promise<unknown>

export interface VisionLineCtx {
  sm: SqlModel
  formNo: string
  parentPk?: Record<string, unknown>
  parentHeaderValues?: Record<string, unknown>
  exec: LineExec
  /** Reported when one of the line's item changes answers an error. */
  onItemChangeError?: (message: string) => void
}

/** An imported Vision line form numbers each line it adds the way Vision's
 *  MasterStatefulEJB.assignNewLineNo does: LINE_NO = the highest line + 1,
 *  blank-padded to a CHAR column's width ("  1"). Only a LINE_NO key that is
 *  not the parent's; none when ``enabled`` is false. */
export function visionLineNoColumn(
  sm: SqlModel, parentPk: Record<string, unknown> | undefined, enabled: boolean,
): ColumnDef | undefined {
  if (!enabled) return undefined
  const parentKeys = new Set(Object.keys(parentPk || {}).map(k => k.toLowerCase()))
  for (const w of sm.columns || []) {
    const c = w?.column
    if (c?.key && columnDbName(c).toLowerCase() === 'line_no'
        && !parentKeys.has('line_no')) return c
  }
  return undefined
}

/** The LINE_NO the next added line takes (``after`` lines past the next). */
export function nextVisionLineNo(
  col: ColumnDef | undefined, rows: Array<Record<string, unknown>>, after = 0,
): string {
  if (!col) return ''
  const key = columnFormKey(col)
  let max = 0
  for (const r of rows) {
    if (r._deleted) continue
    const n = parseInt(String(r[key] ?? '').trim(), 10)
    if (Number.isFinite(n) && n > max) max = n
  }
  const width = String(col.col_type || '').toUpperCase() === 'CHAR'
    ? parseInt(String(col.db_size ?? ''), 10) : NaN
  const no = String(max + 1 + after)
  return Number.isFinite(width) ? no.padStart(width, ' ') : no
}

/** Vision runs, as it writes a returned row in (MasterStatefulEJB.setActionData),
 *  the item change of each column the bl-host listed under ``_item_change``
 *  (isSrvCallOnChg absent / '1' AND an OBJ_ITEMCHANGE row), in the bean's
 *  order — each column set to the bean's value just before its own item change
 *  (applyItemChange). Every OTHER column is written after all of them
 *  (chgDetailStrBuff): adj_iss Stock's amount 0.0 and net_weight 0.3 survive
 *  quantity's and the weights' item changes, as in Vision. The list itself
 *  never reaches the line. */
export async function withVisionItemChanges(
  ctx: VisionLineCtx, line: Record<string, unknown>, r: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const flagged = Array.isArray(r._item_change) ? (r._item_change as string[]) : []
  let out: Record<string, unknown> = { ...line }
  delete out._item_change
  const ran = new Set<string>()
  for (const fieldName of flagged) {
    const col = (ctx.sm.columns || []).find(w => w?.column && columnFormKey(w.column) === fieldName)?.column
    if (!col || !isInvokableRule(normalizeItemChange(col.item_change))) continue
    ran.add(fieldName)
    // The column takes the bean's value first, as if typed in — an earlier
    // item change may have reset it (line_no__sord's blanks lot_no).
    if (fieldName in r) out[fieldName] = r[fieldName]
    try {
      const res = await ctx.exec('item_change', {
        field_name: fieldName, form_no: ctx.formNo, header: out,
        header_values: ctx.parentHeaderValues || {},
      }) as { updates?: Record<string, unknown>; error?: string }
      if (res?.error) ctx.onItemChangeError?.(`item_change failed: ${res.error}`)
      else out = { ...out, ...(res?.updates || {}) }
    } catch {
      // best-effort, like the editor's own item change
    }
  }
  if (flagged.length) {
    for (const [k, v] of Object.entries(r)) {
      if (k !== '_item_change' && !ran.has(k)) out[k] = v
    }
  }
  return out
}

/** The new lines a Vision line action's rows become, set up the way Vision's
 *  MasterStatefulEJB sets up a record it adds (addChildAddDom → updateNewDom):
 *  ``src`` (the blank / new line the button was pressed on, else a blank line
 *  of the form) with every key but the parent's cleared, the form's row_add
 *  defaults (itm_default — run once, they read only the header), then each
 *  row's values; LINE_NO numbered past ``existing``; then the row's item
 *  changes (withVisionItemChanges). */
export async function buildVisionActionLines(
  ctx: VisionLineCtx,
  rows: Array<Record<string, unknown>>,
  src: Record<string, unknown> | null,
  lineCol: ColumnDef | undefined,
  existing: Array<Record<string, unknown>>,
): Promise<Array<Record<string, unknown>>> {
  const base: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(src ?? literalDefaults(ctx.sm))) {
    if (!k.startsWith('__') && !k.startsWith('_')) base[k] = v
  }
  const parentKeys = new Set(Object.keys(ctx.parentPk || {}).map(k => k.toLowerCase()))
  for (const w of ctx.sm.columns || []) {
    const c = w?.column
    if (!c || !c.key) continue
    const fk = columnFormKey(c)
    if (parentKeys.has(columnDbName(c).toLowerCase()) || parentKeys.has(fk.toLowerCase())) continue
    base[fk] = ''
  }
  let defaults: Record<string, unknown> = {}
  if (isInvokableRule(normalizeItemChange(ctx.sm.row_add))) {
    try {
      const res = await ctx.exec('row_add', {
        form_no: ctx.formNo, header: base, header_values: ctx.parentHeaderValues || {},
      }) as { updates?: Record<string, unknown> }
      defaults = res?.updates || {}
    } catch {
      // best-effort, like the editor's own row_add
    }
  }
  const out: Array<Record<string, unknown>> = []
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const line: Record<string, unknown> = { ...base, ...defaults, ...r }
    if (lineCol) line[columnFormKey(lineCol)] = nextVisionLineNo(lineCol, existing, i)
    out.push(await withVisionItemChanges(ctx, line, r))
  }
  return out
}
